/**
 * useTeamChatSocket
 *
 * Low-level socket utilities for team chat:
 * - sendMessage: emit TEAM_MESSAGE and add to local cache optimistically
 * - sendTyping: emit TEAM_TYPING
 * - isTyping: boolean indicating if target user is currently typing
 * - heartbeat: sends TEAM_HEARTBEAT every 4 minutes to keep presence alive
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSocket } from '@/contexts/SocketContext';
import { useAuth } from '@/contexts/AuthContext';
import { SOCKET_EVENTS } from '@troia-v3/types';
import type { TeamMessageEvent, TeamChatMessage, TeamTypingEvent } from '@troia-v3/types';
import { teamChatService } from '../services/teamChatService';

interface SendMessagePayload {
  type: TeamChatMessage['type'];
  content?: string;
  mediaUrl?: string;
  mediaName?: string;
  mediaMimeType?: string;
  mediaSize?: number;
}

export const useTeamChatSocket = (targetUserId: string, roomId: string | null) => {
  const { socket } = useSocket();
  const { state: authState } = useAuth();
  const queryClient = useQueryClient();
  const [isTargetTyping, setIsTargetTyping] = useState(false);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Heartbeat to keep presence alive (every 4 min)
  useEffect(() => {
    if (!socket) return;
    heartbeatRef.current = setInterval(() => {
      socket.emit(SOCKET_EVENTS.TEAM_HEARTBEAT);
    }, 4 * 60 * 1000);
    return () => {
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
    };
  }, [socket]);

  // Listen for typing indicator from the target user
  useEffect(() => {
    if (!socket || !roomId) return;

    const handleTyping = (event: TeamTypingEvent) => {
      if (event.roomId !== roomId || event.userId !== targetUserId) return;
      setIsTargetTyping(event.isTyping);

      // Auto-clear typing indicator after 3s in case "stop" event is missed
      if (event.isTyping) {
        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = setTimeout(() => setIsTargetTyping(false), 3000);
      } else {
        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      }
    };

    socket.on(SOCKET_EVENTS.TEAM_TYPING, handleTyping);
    return () => {
      socket.off(SOCKET_EVENTS.TEAM_TYPING, handleTyping);
    };
  }, [socket, roomId, targetUserId]);

  const sendMessage = useCallback(
    async (payload: SendMessagePayload) => {
      if (!socket || !roomId || !authState.user) return;

      const message: TeamChatMessage = {
        id: crypto.randomUUID(),
        roomId,
        senderId: authState.user.id,
        senderName: `${authState.user.firstName} ${authState.user.lastName}`,
        senderAvatar: authState.user.avatar,
        readBy: [authState.user.id],
        createdAt: new Date().toISOString(),
        ...payload,
      };

      // Optimistic update — add to local cache immediately
      queryClient.setQueryData<{ messages: TeamChatMessage[]; roomId: string }>(
        ['team-chat', 'messages', targetUserId],
        (old) => {
          if (!old) return { messages: [message], roomId };
          const exists = old.messages.some((m) => m.id === message.id);
          if (exists) return old;
          return { ...old, messages: [...old.messages, message] };
        }
      );

      // Emit via socket
      const event: TeamMessageEvent = { roomId, message };
      socket.emit(SOCKET_EVENTS.TEAM_MESSAGE, event);
    },
    [socket, roomId, authState.user, queryClient, targetUserId]
  );

  const sendTyping = useCallback(
    (isTyping: boolean) => {
      if (!socket || !roomId) return;
      socket.emit(SOCKET_EVENTS.TEAM_TYPING, { roomId, userId: authState.user?.id, isTyping });
    },
    [socket, roomId, authState.user?.id]
  );

  const uploadAndSend = useCallback(
    async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      const result = await teamChatService.uploadFile(targetUserId, formData);
      const type: TeamChatMessage['type'] = result.mimeType.startsWith('image/')
        ? 'image'
        : result.mimeType.startsWith('audio/')
        ? 'audio'
        : 'file';
      await sendMessage({
        type,
        mediaUrl: result.url,
        mediaName: result.fileName,
        mediaMimeType: result.mimeType,
        mediaSize: result.size,
      });
    },
    [sendMessage, targetUserId]
  );

  return { sendMessage, sendTyping, uploadAndSend, isTargetTyping };
};
